import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faX } from "@fortawesome/free-solid-svg-icons";
import { withAppHOC } from "../../hoc";
import {
  StyledContentContainer,
  StyledFiltersContainer,
  StyledFilters,
  StyledFilterContainer,
  StyledSearchButton,
  StyledResetButton,
  StyledStatusPill,
} from "../../components/styled";
import PageHeader from "../../components/PageHeader/PageHeader";
import { MessageBox, Dropdown, TextInput } from "../../components/FormElements";
import Loader from "../../components/Loader/Loader";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import NoData from "../../components/NoData/NoData";
import { TableBuilder } from "../../components/TableElements";
import Pagination from "../../components/Pagination/Pagination";
import API from "../../api";
import { RESPONSE_STATUSES } from "../../constants";
import { formatCurrency, getSearchParams } from "../../utils";
import FiltersButton from "../../components/FiltersButton/FiltersButton";

const BuyerWrongToRightTransactionsList = () => {
  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };
  const [filters, setFilters] = useState({
    fTransactionsId: "",
    fRechargeNumber: "",
    fStatus: "",
    fFromDate: "",
    fToDate: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams("pageNumber") || 1,
    recordsPerPage: getSearchParams("recordsPerPage") || 30,
  });

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const readData = async () => {
    try {
      const response = await API.get(
        `/buyer/wrong-to-right-transactions?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fTransactionsId=${appliedFilters.fTransactionsId}&fRechargeNumber=${appliedFilters.fRechargeNumber}&fFromDate=${appliedFilters.fFromDate}&fToDate=${appliedFilters.fToDate}&fStatus=${appliedFilters.fStatus}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: [
      "buyer_wrong_to_right_transactions",
      paginationData,
      appliedFilters,
    ],
    queryFn: readData,
  });

  const reloadData = async () => await refetch();

  const filterData = async (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
  };

  const clearFilters = async (e) => {
    e.preventDefault();
    setFilters({
      fTransactionsId: "",
      fRechargeNumber: "",
      fStatus: "",
      fFromDate: "",
      fToDate: "",
    });
    setAppliedFilters({
      fTransactionsId: "",
      fRechargeNumber: "",
      fStatus: "",
      fFromDate: "",
      fToDate: "",
    });
  };

  const renderPageHeading = () => (
    <PageHeader
      title="Wrong To Right Transactions"
      showAddButton={false}
      showReloadData={true}
      reloadData={reloadData}
    />
  );

  const statusOptions = [
    {
      name: "All",
      value: "",
    },
    {
      name: "Pending",
      value: "Pending",
    },
    {
      name: "Approved",
      value: "Approved",
    },
    {
      name: "Rejected",
      value: "Rejected",
    },
  ];

  if (isLoading) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <LoadingSpinner />
        </StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={isRefetching} />
      {renderPageHeading()}
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledContentContainer graybackground="true">
        <StyledFiltersContainer className={showFilters ? "show" : ""}>
          <StyledFilters>
            <StyledFilterContainer>
              <TextInput
                value={filters.fTransactionsId}
                onChange={(value) =>
                  handleFilterChange("fTransactionsId", value)
                }
                placeholder="Transaction ID"
                disabled={isLoading}
              />
              <TextInput
                value={filters.fRechargeNumber}
                onChange={(value) =>
                  handleFilterChange("fRechargeNumber", value)
                }
                placeholder="Recharge Number"
                disabled={isLoading}
              />
              <Dropdown
                value={filters.fStatus}
                onChange={(value) => handleFilterChange("fStatus", value)}
                placeholder="Status"
                disabled={isLoading}
                options={statusOptions}
              />
              <TextInput
                type="date"
                value={filters.fFromDate}
                onChange={(value) => handleFilterChange("fFromDate", value)}
                placeholder="From Date"
                disabled={isLoading}
              />
              <TextInput
                type="date"
                value={filters.fToDate}
                onChange={(value) => handleFilterChange("fToDate", value)}
                placeholder="To Date"
                disabled={isLoading}
              />
            </StyledFilterContainer>
            <StyledSearchButton onClick={filterData} type="button">
              <FontAwesomeIcon icon={faSearch} />
              Search
            </StyledSearchButton>
            <StyledResetButton onClick={clearFilters} type="button">
              <FontAwesomeIcon icon={faX} />
              Clear
            </StyledResetButton>
          </StyledFilters>
        </StyledFiltersContainer>
        {data?.status === RESPONSE_STATUSES.FAILED && (
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={data?.message}
          />
        )}
        {data?.data?.length === 0 ? (
          <NoData />
        ) : (
          <>
            <TableBuilder
              isLoading={isLoading}
              tableHeadings={[
                {
                  title: "Transaction ID",
                  dataSelector: "transactionsId",
                  dataType: "string",
                  textAlign: "center",
                },
                {
                  title: "Wrong Account Number",
                  dataSelector: "wrongAccountNumber",
                  dataType: "string",
                },
                {
                  title: "Reason",
                  dataSelector: "reason",
                  dataType: "string",
                },
                {
                  title: "Remark",
                  dataSelector: "remark",
                  dataType: "string",
                  cellrenderer: (value) => value ?? "N/A",
                },
                {
                  title: "Right Account Number",
                  dataSelector: "rightAccountNumber",
                  dataType: "string",
                },
               {
                  title: "Operator Id",
                  dataSelector: "operatorId",
                  dataType: "string",
                  cellrenderer: (value) => value ?? "N/A",
                },
                {
                  title: "Amount",
                  dataSelector: "amount",
                  dataType: "string",
                  cellrenderer: (value) => formatCurrency(value),
                },
                {
                  title: "Seller Remark",
                  dataSelector: "sellersRemark",
                  dataType: "string",
                  cellrenderer: (value) => value ?? "N/A",
                },
                {
                  title: "Status",
                  dataSelector: "status",
                  dataType: "string",
                  textAlign: "center",
                  cellrenderer: (value) => (
                    <StyledStatusPill
                      className={
                        value === "Approved"
                          ? "green"
                          : value === "Rejected"
                          ? "red"
                          : "yellow"
                      }
                    >
                      {value}
                    </StyledStatusPill>
                  ),
                },
                {
                  title: "Complaint Raised Time",
                  dataSelector: "createdOn",
                  dataType: "string",
                  cellrenderer: (value) => (value ? value : "N/A"),
                },
                {
                  title: "Complaint Resolution Time",
                  dataSelector: "updatedOn",
                  dataType: "string",
                  cellrenderer: (value) => (value ? value : "N/A"),
                },
              ]}
              tableData={data?.data}
            />
            <Pagination
              totalRecords={data?.totalRecords}
              paginationData={paginationData}
              setPaginationData={setPaginationData}
            />
          </>
        )}
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(BuyerWrongToRightTransactionsList);
